import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator,
  BookOpen,
  Atom,
  Globe,
  Scroll,
  Translate,
  ArrowRight,
  Spinner,
  Lock,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import LessonCountShowcase from '../components/LessonCountShowcase';
import SEO from '../components/SEO';

import API_URL from '../utils/apiUrl';
import { STARTER_ROUTES } from '../utils/starterRoutes';

const AVAILABLE_SLUGS = ['maths', 'english-language', 'combined-science', 'geography'];

const iconMap = {
  Calculator,
  BookOpen,
  Atom,
  Globe,
  Scroll,
  Translate,
};

const colorMap = {
  indigo: 'bg-indigo-500',
  rose: 'bg-rose-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  cyan: 'bg-cyan-500',
  orange: 'bg-orange-500',
  blue: 'bg-blue-500',
  red: 'bg-red-500',
};

function getSubjectPath(slug) {
  const previewKey =
    slug === 'english-language'
      ? 'english'
      : slug === 'combined-science'
        ? 'science'
        : slug;

  if (STARTER_ROUTES[previewKey]) {
    return `/free-lesson?subject=${previewKey}`;
  }

  switch (slug) {
    case 'maths':
      return '/maths';
    case 'english-language':
      return '/english';
    case 'combined-science':
      return '/science';
    case 'geography':
      return '/subjects/geography';
    default:
      return `/subjects/${slug}`;
  }
}

export default function Subjects() {
  const { loading: authLoading } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subjects`);
        setSubjects(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching subjects:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <SEO
        title="GCSE Subjects — Maths, English, Science & Geography"
        description="Explore Grade9Ready's GCSE subjects: Maths, English Language and Literature, Science and Geography. AI-marked practice, adaptive levels and parent progress tracking."
        path="/subjects"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            className="inline-flex items-center gap-2 bg-accent border-2 border-foreground rounded-full px-4 py-2 mb-5 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <BookOpen size={16} weight="fill" />
            <span className="text-xs sm:text-sm font-black uppercase tracking-wide">
              Maths, English, Science and Geography live now
            </span>
          </motion.div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4">
            Choose Your Subject
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Pick a live subject to explore sections, topics and practice activities. Maths,
            English, Science and Geography are available now, with History and Spanish planned
            next.
          </p>
        </div>

        {/* Lessons available visual */}
        <LessonCountShowcase
          heading="What's live right now"
          subheading="Maths, English, Science and Geography are live, with structured sections, topic practice, worked examples, AI feedback and parent progress tracking. History and Spanish are on the roadmap."
        />

        {/* Subject filter tags */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {['All Subjects', 'Core', 'EBacc', 'Languages'].map((tag) => (
            <button
              key={tag}
              type="button"
              className={`px-4 py-2 rounded-lg border-2 border-foreground font-bold text-sm transition-colors ${
                tag === 'All Subjects'
                  ? 'bg-primary text-white shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]'
                  : 'bg-card hover:bg-secondary'
              }`}
              data-testid={`filter-${tag.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Subjects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {subjects.map((subject, index) => {
            const Icon = iconMap[subject.icon] || BookOpen;
            const bgColor = colorMap[subject.color] || 'bg-indigo-500';
            const isAvailable = AVAILABLE_SLUGS.includes(subject.slug);
            const subjectPath = getSubjectPath(subject.slug);

            return (
              <motion.div
                key={subject.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.08 }}
              >
                {isAvailable ? (
                  <Link
                    to={subjectPath}
                    className="neo-card block h-full hover:-translate-y-1 transition-all group"
                    data-testid={`subject-${subject.slug}`}
                  >
                    <div
                      className={`w-16 h-16 ${bgColor} rounded-xl border-2 border-foreground flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]`}
                    >
                      <Icon size={32} weight="bold" className="text-white" />
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="font-heading font-bold text-xl">{subject.name}</h2>
                      <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        Live
                      </span>
                    </div>

                    <p className="text-muted-foreground text-sm mb-4">
                      {subject.description}
                    </p>

                    <div className="flex items-center gap-2 text-primary font-bold text-sm group-hover:gap-3 transition-all">
                      Explore
                      <ArrowRight size={16} weight="bold" />
                    </div>
                  </Link>
                ) : (
                  <div
                    className="neo-card block h-full opacity-50 cursor-not-allowed relative"
                    data-testid={`subject-${subject.slug}`}
                  >
                    <div className="absolute top-3 right-3 px-2 py-1 bg-muted rounded-lg border border-border">
                      <span className="text-xs font-bold text-muted-foreground">
                        Coming Soon
                      </span>
                    </div>

                    <div className="w-16 h-16 bg-gray-400 rounded-xl border-2 border-foreground/50 flex items-center justify-center mb-4">
                      <Lock size={32} weight="bold" className="text-white" />
                    </div>

                    <h2 className="font-heading font-bold text-xl mb-2 text-muted-foreground">
                      {subject.name}
                    </h2>

                    <p className="text-muted-foreground text-sm mb-4">
                      {subject.description}
                    </p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* GCSE Revision Hubs */}
        <div className="mt-16" data-testid="subjects-revision-hubs">
          <h3 className="font-heading font-bold text-2xl mb-2 text-center">
            GCSE revision guides
          </h3>

          <p className="text-center text-muted-foreground mb-6 max-w-2xl mx-auto">
            Deep-dive revision pages with topic coverage, examiner tips and FAQs for each live
            GCSE subject.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <Link
              to="/gcse-maths-revision"
              data-testid="revision-hub-maths"
              className="neo-card p-5 hover:-translate-y-0.5 transition-all border-indigo-200"
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-indigo-700 mb-1">
                Revision guide
              </div>
              <h4 className="font-heading font-bold text-lg mb-1">GCSE Maths Revision</h4>
              <p className="text-sm text-muted-foreground">
                Foundation + Higher · Algebra, Geometry, Probability and problem mode.
              </p>
            </Link>

            <Link
              to="/gcse-english-revision"
              data-testid="revision-hub-english"
              className="neo-card p-5 hover:-translate-y-0.5 transition-all border-rose-200"
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-rose-700 mb-1">
                Revision guide
              </div>
              <h4 className="font-heading font-bold text-lg mb-1">GCSE English Revision</h4>
              <p className="text-sm text-muted-foreground">
                Language, Literature, essay practice, examiner guidance and AI feedback.
              </p>
            </Link>

            <Link
              to="/gcse-science-revision"
              data-testid="revision-hub-science"
              className="neo-card p-5 hover:-translate-y-0.5 transition-all border-emerald-200"
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-emerald-700 mb-1">
                Revision guide
              </div>
              <h4 className="font-heading font-bold text-lg mb-1">GCSE Science Revision</h4>
              <p className="text-sm text-muted-foreground">
                Biology, Chemistry, Physics and required practicals.
              </p>
            </Link>

            <Link
              to="/subjects/geography"
              data-testid="revision-hub-geography"
              className="neo-card p-5 hover:-translate-y-0.5 transition-all border-cyan-200"
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-cyan-700 mb-1">
                Live subject
              </div>
              <h4 className="font-heading font-bold text-lg mb-1">GCSE Geography Revision</h4>
              <p className="text-sm text-muted-foreground">
                Physical geography, human geography, fieldwork, map skills and AQA-style exam
                practice.
              </p>
            </Link>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-16 neo-card max-w-3xl mx-auto text-center">
          <h3 className="font-heading font-bold text-xl mb-4">Structured for Success</h3>

          <p className="text-muted-foreground mb-6">
            Each subject is broken down into sections, topics and individual learning tasks.
            Lessons include explanations, worked examples, practice activities and feedback so
            students can build knowledge actively rather than just reading notes.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <span className="neo-badge bg-accent">Year 7-11 Support</span>
            <span className="neo-badge bg-primary text-white">GCSE Aligned</span>
            <span className="neo-badge bg-emerald-500 text-white">AI Feedback</span>
            <span className="neo-badge bg-cyan-500 text-white">Geography Live</span>
          </div>
        </div>
      </div>
    </div>
  );
}